import type {
  ResetConfigurationRequest,
  UpdateConfigurationRequest
} from '@/domains/configuration/types/requests/UpdateConfigurationRequest.types'
import type {
  ConfigurationResponse,
  ResetConfigurationResponse
} from '@/domains/configuration/types/responses/ConfigurationResponses.types'
import type { ConfigurationTraceResponse } from '@/domains/configuration/types/responses/ConfigurationTraceResponse.types'
import type { GenericEntitiesResponse } from '@/domains/configuration/types/responses/GenericEntitiesResponse.types'

import { twineServiceClient } from '@/services/api'
import { relativeZiggyRoute } from '@/utils/url-util/urlUtil'

/**
 * Configuration API Collection
 *
 * Backend Controller: App\Admin\Http\Domains\Configuration\Controllers\ConfigurationController
 */
export const callConfiguration = () => {
  /**
   * Returns the full ConfigurationStructure (key_definitions +
   * hierarchy_definitions + current_values). The single source of truth for
   * the UI — domain summary, per-domain slicing, and resolution traces are
   * all derived client-side from this payload.
   *
   * Pass a scope to resolve `current_values` at that hierarchy node instead of
   * the tenant root (e.g. `{ scope_type: 'location', scope_id: '4' }` — the
   * location fiscal panel reads its identity this way).
   */
  const index = (scope?: { scope_type: string; scope_id: string }) =>
    twineServiceClient.get<ConfigurationResponse>({
      endpoint: relativeZiggyRoute('admin.configuration.index'),
      ...(scope ? { queryParams: { scope_type: scope.scope_type, scope_id: scope.scope_id } } : {})
    })

  /**
   * Returns the resolved IDs of "generic" marketplace fallback entities
   * (currently a generic menu item and generic addon group) so the restaurant
   * delete flow can block deletion of the system fallbacks used by Bolt/Wolt.
   *
   * Kept as a dedicated endpoint because the IDs come from configuration but
   * we don't want every consumer pulling the full ConfigurationStructure.
   */
  const genericEntities = () =>
    twineServiceClient.get<GenericEntitiesResponse>({
      endpoint: relativeZiggyRoute('admin.configuration.generic-entities')
    })

  const update = (body: UpdateConfigurationRequest) =>
    twineServiceClient.put<void>({
      endpoint: relativeZiggyRoute('admin.configuration.update'),
      body
    })

  const reset = (request: ResetConfigurationRequest) => {
    const queryParams: Record<string, string> = { key: request.key }
    if (request.model_type != null) queryParams.model_type = request.model_type
    if (request.model_id != null) queryParams.model_id = request.model_id

    return twineServiceClient.delete<ResetConfigurationResponse>({
      endpoint: relativeZiggyRoute('admin.configuration.reset'),
      queryParams
    })
  }

  /**
   * Returns the full resolution trace for a single configuration key at the
   * given scope. Lazily fetched when the user opens the "Show resolution"
   * expander in the admin Configuration page — never loaded eagerly with the
   * index payload. Exposes every stored override along the hierarchy chain,
   * including out-of-chain siblings (e.g. overrides at the payment_method
   * level when the user is editing at tenant scope).
   */
  const trace = (params: { key: string; scope_type?: string; scope_id?: string }) =>
    twineServiceClient.get<ConfigurationTraceResponse>({
      endpoint: relativeZiggyRoute('admin.configuration.trace'),
      queryParams: {
        key: params.key,
        ...(params.scope_type ? { scope_type: params.scope_type } : {}),
        ...(params.scope_id ? { scope_id: params.scope_id } : {})
      }
    })

  return {
    index,
    genericEntities,
    update,
    reset,
    trace
  }
}
